import { Outlet, useLocation, Link, NavLink, useNavigate, matchPath } from "react-router-dom";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { MenuIcon, CloseIcon } from "./Icon";
import { ThemeToggle } from "./ThemeToggle";
import { EmailVerificationBanner } from "./EmailVerificationBanner";
import { useTranslation } from "react-i18next";
import { getApiBase, setApiBase } from "@/lib/config";
import { pingBackend } from "@/lib/api";
import { useAuthUser, signOut } from "@/lib/auth";
import { STORAGE_UI } from "@/constants/storage";
import { ROUTES } from "@/constants/routes";
import {
  BrandGlyph,
  NavGlyph,
  SHELL_TOKENS,
  TYPE_BADGE,
  UrgencyDot,
  type GlyphName,
} from "./agent-pro/ui";
import { FlowsProvider, ProOnboardingGate, useFlows } from "./agent-pro/flows";
import { useCases } from "@/lib/agentCases";

type NavItem = {
  to: string;
  labelKey: string;
  fallback: string;
  icon: GlyphName;
  badge?: number;
};

// Primary group — ordered by daily-usage frequency. Cases is the agent's
// primary work surface (rendered separately as an expandable header) and
// sits at the top of the nav; Dashboard comes next as the "what's urgent
// today" overview; Inbox + Leads (both "incoming I need to respond to")
// sit before Reports (weekly cadence). The one tool-shaped surface with no
// time pressure (Seller Pitch) lives below in TOOL_ITEMS, default-collapsed
// so it doesn't dilute the primary surface.
const PRIMARY_ITEMS: NavItem[] = [
  // Cases is rendered separately with the expandable behavior — it
  // anchors the top of the nav, not this array. Listed here as a
  // comment for inventory only.
  { to: "/pro/dashboard", labelKey: "agentNav.dashboard", fallback: "Dashboard", icon: "home" },
  { to: "/pro/inbox",     labelKey: "agentNav.aiInbox",   fallback: "AI Inbox",  icon: "inbox" },
  { to: "/pro/leads",     labelKey: "agentNav.chatLeads", fallback: "Chat Leads", icon: "inbox" },
  { to: "/pro/reports",   labelKey: "agentNav.reports",   fallback: "Reports",   icon: "report" },
  // Agent-facing article authoring is hidden for now — the buyer-facing
  // Explore › Newsroom feed, the public /author + /news pages, and the
  // admin editorial portal all stay live; only the self-serve authoring
  // entry (/pro/articles*, see router.tsx) is unlinked. Re-add this row
  // and the routes to bring it back.
];

// Pipeline subgroup — the cross-case mandate surfaces (Listings / Offers /
// Calendar) that the case-centric IA keeps as standalone aggregator pages.
// Default-collapsed inside the Workspace section so they're one toggle away
// without diluting the primary daily surface (Cases / Dashboard / Inbox).
// These dedicated flat routes superseded the legacy agent-workspace
// sub-shell torn down in PROP-222.
const PIPELINE_ITEMS: NavItem[] = [
  { to: ROUTES.proListings, labelKey: "agentNav.listings", fallback: "Listings", icon: "building" },
  { to: ROUTES.proOffers,   labelKey: "agentNav.offers",   fallback: "Offers",   icon: "handshake" },
  { to: ROUTES.proCalendar, labelKey: "agentNav.calendar", fallback: "Calendar", icon: "cal" },
];

// Secondary tools — the utilities the case-centric IA doesn't subsume.
// Folded into a default-collapsed group so they don't dilute the primary
// surface. Trimmed for launch in PROP-238: Brand Studio was cut, Market
// Watch's agent entry was removed (the page still lives at /market-watch
// for the buyer surface), and Content Factory was folded into the
// listing-detail "AI copy" drawer (generates from the listing's real
// data). Seller Pitch is the one dedicated tool page that survives here.
//
// Removed from the sidebar (some URLs still routed for deep links):
//   - "AI Copilot": replaced by the persistent CaseAIRail rendered
//     on each case page; portfolio-wide chat isn't a surface in this IA.
//   - "Lead CRM": cases ARE the CRM in the new IA.
//   - "Client Report": the Reports hub already surfaces every
//     case-derived report.
const TOOL_ITEMS: NavItem[] = [
  { to: "/pro/pitch", labelKey: "agentNav.pitch", fallback: "Seller Pitch", icon: "handshake" },
];

function AgentSidebar({ onNavigate, onCollapse }: { onNavigate?: () => void; onCollapse?: () => void }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const flows = useFlows();
  const [apiUrl, setApiUrlLocal] = useState(getApiBase());
  const [status, setStatus] = useState<string>(t("home.checking"));
  const [statusTone, setStatusTone] = useState<"muted" | "good" | "bad">("muted");
  const [showConfig, setShowConfig] = useState(false);
  const [casesExpanded, setCasesExpanded] = useState(true);
  const [pipelineExpanded, setPipelineExpanded] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);

  useEffect(() => {
    void refreshPing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshPing() {
    setStatus(t("home.checking"));
    setStatusTone("muted");
    const r = await pingBackend();
    if (r.ok) {
      setStatus(t("home.connected", { count: r.tools ?? 0 }));
      setStatusTone("good");
    } else {
      setStatus(t("home.unreachable"));
      setStatusTone("bad");
    }
  }

  function onApiChange(value: string) {
    setApiUrlLocal(value);
    setApiBase(value);
    void refreshPing();
  }

  function toggleLang() {
    const next = i18n.language?.startsWith("zh") ? "en" : "zh";
    void i18n.changeLanguage(next);
  }

  const statusColor =
    statusTone === "good" ? "var(--ag-accent)" : statusTone === "bad" ? "#bf3838" : "var(--ag-ink-3)";

  // Sidebar shows live cases — fetched on every shell render. Acceptable
  // because /agent/cases is cheap (single SELECT) and the user rarely
  // remounts the shell. A page-level cache is the next iteration if
  // request volume becomes a concern.
  const { cases: liveCases } = useCases();
  const activeCases = liveCases.filter((c) => c.stage !== "Closed Won" && c.stage !== "Closed Lost");
  const casesPath = matchPath("/pro/cases/:id", location.pathname) || matchPath("/pro/cases", location.pathname);
  const activeCaseId = matchPath("/pro/cases/:id", location.pathname)?.params.id;
  // Highlight the collapsed Pipeline header when the active route is one of
  // its (collapsed-away) children, so the agent still sees where they are.
  const pipelineActive = PIPELINE_ITEMS.some(
    (it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/"),
  );

  return (
    <aside
      style={{
        ...SHELL_TOKENS,
        width: 264,
        flexShrink: 0,
        height: "100%",
        background: "var(--ag-surface)",
        borderRight: "1px solid var(--ag-rule)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "var(--ag-font-ui)",
        color: "var(--ag-ink)",
      }}
    >
      {/* Brand row */}
      <div
        style={{
          padding: "18px 16px",
          borderBottom: "1px solid var(--ag-rule)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <NavLink to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
          <BrandGlyph />
          <div style={{ lineHeight: 1 }}>
            <div
              style={{
                fontFamily: "var(--ag-font-serif)",
                fontSize: 17,
                fontWeight: 600,
                color: "var(--ag-ink)",
                letterSpacing: "-0.02em",
              }}
            >
              {t("app.name")}
              <span style={{ color: "var(--ag-accent)" }}>.</span>
            </div>
            <div
              style={{
                fontFamily: "var(--ag-font-ui)",
                fontSize: 9.5,
                marginTop: 2,
                color: "var(--ag-ink-3)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {t("agentNav.brandTagline", { defaultValue: "Agent Workspace" })}
            </div>
          </div>
        </NavLink>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={() => {
              onNavigate?.();
              flows.openNewCase();
            }}
            title={t("agent.flows.newCase.openTitle", { defaultValue: "New case" })}
            aria-label={t("agent.flows.newCase.openTitle", { defaultValue: "New case" })}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "1px solid var(--ag-rule)",
              background: "transparent",
              color: "var(--ag-ink-3)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NavGlyph name="plus" size={13} />
          </button>
          <ThemeToggle />
          <button
            onClick={toggleLang}
            title={t("nav.language")}
            style={{
              border: "1px solid var(--ag-rule)",
              background: "transparent",
              color: "var(--ag-ink-3)",
              padding: "3px 7px",
              borderRadius: 6,
              fontSize: 10.5,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--ag-font-ui)",
            }}
          >
            {i18n.language?.startsWith("zh") ? "EN" : "中"}
          </button>
        </div>
      </div>

      {/* Body — Workspace primary group + expandable Cases + Tools */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
        <SectionLabel>{t("agentNav.sectionWorkspace", { defaultValue: "Workspace" })}</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Cases first — the agent's primary daily surface. Dashboard
              follows as the "what needs attention" overview. Ordering
              reflects how often an agent opens each tab in a typical day. */}
          <ExpandableNavHeader
            icon="cases"
            label={t("agentNav.cases", { defaultValue: "Cases" })}
            count={activeCases.length}
            expanded={casesExpanded}
            active={!!casesPath}
            onToggle={() => setCasesExpanded((v) => !v)}
          />
          {casesExpanded && (
            <div
              style={{
                paddingLeft: 22,
                paddingBottom: 4,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <NavLink
                to="/pro/cases"
                end
                onClick={onNavigate}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "5px 8px",
                  borderRadius: 5,
                  textDecoration: "none",
                  background: isActive ? "rgba(29,74,58,0.06)" : "transparent",
                  color: isActive ? "var(--ag-ink)" : "var(--ag-ink-3)",
                  fontFamily: "var(--ag-font-serif)",
                  fontStyle: "italic",
                  fontSize: 12,
                  fontWeight: isActive ? 500 : 400,
                  letterSpacing: "-0.005em",
                })}
              >
                All cases
              </NavLink>
              {/* R9: cap the inline list at 12 to avoid an unbounded
                  sidebar scroll for power-agents. The "All cases"
                  link above takes you to the full portfolio if you
                  need more. */}
              {activeCases.slice(0, 12).map((c) => {
                const t = TYPE_BADGE[c.type];
                const isActive = activeCaseId === c.id;
                return (
                  <Link
                    key={c.id}
                    to={`/pro/cases/${c.id}`}
                    onClick={onNavigate}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "5px 8px",
                      borderRadius: 5,
                      textDecoration: "none",
                      background: isActive ? "rgba(29,74,58,0.06)" : "transparent",
                      color: isActive ? "var(--ag-ink)" : "var(--ag-ink-2)",
                      fontFamily: "var(--ag-font-ui)",
                      fontSize: 12,
                      fontWeight: isActive ? 500 : 400,
                      letterSpacing: "-0.005em",
                    }}
                  >
                    <UrgencyDot urgency={c.urgency} />
                    <span style={{ color: t.ink, opacity: 0.7, display: "flex", flex: "none" }}>
                      <NavGlyph name={t.icon} size={11} />
                    </span>
                    <span
                      style={{
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.shortName}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Dashboard / Inbox / Leads / Reports — full PRIMARY_ITEMS
              now that Cases is the expandable header above. Each item
              is one click and surfaces a top-of-funnel signal: AI
              attention list, incoming chat sources, weekly report
              cadence. */}
          {PRIMARY_ITEMS.map((it) => (
            <NavRow
              key={it.to}
              to={it.to}
              icon={it.icon}
              label={t(it.labelKey, { defaultValue: it.fallback })}
              badge={it.badge}
              onNavigate={onNavigate}
            />
          ))}

          {/* Pipeline — cross-case mandate surfaces (Listings / Offers /
              Calendar) folded into a default-collapsed subgroup. Mirrors
              the expandable Cases header above; one toggle reveals the
              standalone aggregator pages without crowding the daily nav. */}
          <ExpandableNavHeader
            icon="pipeline"
            label={t("agentNav.pipeline", { defaultValue: "Pipeline" })}
            expanded={pipelineExpanded}
            active={pipelineActive}
            onToggle={() => setPipelineExpanded((v) => !v)}
          />
          {pipelineExpanded && (
            <div style={{ paddingLeft: 22, paddingBottom: 4, display: "flex", flexDirection: "column", gap: 1 }}>
              {PIPELINE_ITEMS.map((it) => (
                <NavRow
                  key={it.to}
                  to={it.to}
                  icon={it.icon}
                  label={t(it.labelKey, { defaultValue: it.fallback })}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}
        </div>

        <SectionLabel pad="14px 4px 8px">
          <button
            onClick={() => setToolsExpanded((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: "var(--ag-ink-4)",
              fontSize: 9.5,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "var(--ag-font-ui)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t("agentNav.tools", { defaultValue: "Tools" })}{" "}
            <span style={{ fontSize: 8 }}>{toolsExpanded ? "▾" : "▸"}</span>
          </button>
        </SectionLabel>
        {toolsExpanded && (
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {TOOL_ITEMS.map((it) => (
              <NavRow
                key={it.to}
                to={it.to}
                icon={it.icon}
                label={t(it.labelKey, { defaultValue: it.fallback })}
                onNavigate={onNavigate}
                muted
              />
            ))}
          </div>
        )}
      </nav>

      {/* Backend / settings strip */}
      <div style={{ borderTop: "1px solid var(--ag-rule)", padding: "10px 14px 4px" }}>
        <button
          onClick={() => setShowConfig((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "100%",
            background: "transparent",
            border: "none",
            color: "var(--ag-ink-3)",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 500,
            fontFamily: "var(--ag-font-ui)",
            padding: "2px 0",
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 999, background: statusColor, flex: "none" }} />
          <span style={{ flex: 1, textAlign: "left" }}>{t("nav.backend")}</span>
          <span style={{ color: "var(--ag-ink-4)", fontSize: 9.5 }}>{showConfig ? "−" : "+"}</span>
        </button>
        {showConfig && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 10, color: "var(--ag-ink-4)" }}>{t("home.apiUrl")}</label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => onApiChange(e.target.value)}
              style={{
                width: "100%",
                background: "var(--ag-surface-2)",
                border: "1px solid var(--ag-rule)",
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 11,
                color: "var(--ag-ink)",
                fontFamily: "var(--ag-font-mono)",
                outline: "none",
              }}
            />
            <div style={{ fontSize: 10.5, color: statusColor }}>{status}</div>
          </div>
        )}
        <Link
          to="/chat"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 8,
            fontSize: 10.5,
            color: "var(--ag-ink-3)",
            textDecoration: "none",
          }}
        >
          ↗ {t("agentWorkspace.switchToBuyer", { defaultValue: "Switch to Buyer view" })}
        </Link>
      </div>

      <AgentProfileFooter />

      {onCollapse && (
        <div
          className="hidden md:flex"
          style={{
            flex: "none",
            justifyContent: "flex-end",
            padding: "6px 10px",
            borderTop: "1px solid var(--ag-rule)",
            background: "var(--ag-surface)",
          }}
        >
          <button
            onClick={onCollapse}
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "none",
              background: "transparent",
              color: "var(--ag-ink-3)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
              <rect x="1.5" y="2.5" width="11" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
              <path d="M5.5 2.5v9" stroke="currentColor" strokeWidth="1.3" />
              <path d="M9.5 5l-1.5 2 1.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
    </aside>
  );
}

function NavRow({
  to,
  icon,
  label,
  badge,
  onNavigate,
  muted,
  end,
}: {
  to: string;
  icon: GlyphName;
  label: string;
  badge?: number;
  onNavigate?: () => void;
  muted?: boolean;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      end={end}
      style={({ isActive }) => ({
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 8px",
        borderRadius: 6,
        textDecoration: "none",
        background: isActive ? "var(--ag-accent-soft)" : "transparent",
        color: isActive ? "var(--ag-ink)" : muted ? "var(--ag-ink-3)" : "var(--ag-ink-2)",
        fontFamily: "var(--ag-font-ui)",
        fontSize: 12.5,
        fontWeight: isActive ? 500 : 400,
        letterSpacing: "-0.005em",
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              style={{
                position: "absolute",
                left: -12,
                top: 4,
                bottom: 4,
                width: 2,
                background: "var(--ag-accent)",
                borderRadius: 2,
              }}
            />
          )}
          <span
            style={{
              color: isActive ? "var(--ag-accent)" : "var(--ag-ink-3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            <NavGlyph name={icon} size={13} />
          </span>
          <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
          </span>
          {badge != null && (
            <span
              style={{
                background: "#bf3838",
                color: "white",
                fontSize: 9.5,
                fontWeight: 600,
                padding: "1px 5px",
                borderRadius: 3,
                lineHeight: 1.3,
              }}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function ExpandableNavHeader({
  icon,
  label,
  count,
  expanded,
  active,
  onToggle,
}: {
  icon: GlyphName;
  label: string;
  count?: number;
  expanded: boolean;
  active?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      style={{
        position: "relative",
        width: "100%",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 8px",
        border: "none",
        borderRadius: 6,
        background: active && !expanded ? "var(--ag-accent-soft)" : "transparent",
        color: active ? "var(--ag-ink)" : "var(--ag-ink-2)",
        cursor: "pointer",
        fontFamily: "var(--ag-font-ui)",
        fontSize: 12.5,
        fontWeight: active ? 500 : 400,
        letterSpacing: "-0.005em",
      }}
    >
      {active && !expanded && (
        <span
          style={{
            position: "absolute",
            left: -12,
            top: 4,
            bottom: 4,
            width: 2,
            background: "var(--ag-accent)",
            borderRadius: 2,
          }}
        />
      )}
      <span style={{ color: active ? "var(--ag-accent)" : "var(--ag-ink-3)", display: "flex" }}>
        <NavGlyph name={icon} size={13} />
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {count != null && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 600,
            color: "var(--ag-ink-3)",
            background: "var(--ag-surface-2)",
            padding: "1px 5px",
            borderRadius: 3,
          }}
        >
          {count}
        </span>
      )}
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        style={{
          transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
          transition: "transform 150ms",
          color: "var(--ag-ink-4)",
          flex: "none",
        }}
      >
        <path
          d="M2 4l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function SectionLabel({ children, pad = "6px 4px 8px" }: { children: ReactNode; pad?: string }) {
  return (
    <div
      style={{
        fontFamily: "var(--ag-font-ui)",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "var(--ag-ink-4)",
        padding: pad,
      }}
    >
      {children}
    </div>
  );
}

function agentInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function AgentProfileFooter() {
  const { user } = useAuthUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  async function handleSignOut() {
    setMenuOpen(false);
    await signOut();
    navigate("/auth");
  }

  if (!user) {
    return (
      <div style={{ flex: "none", padding: "12px 14px", borderTop: "1px solid var(--ag-rule)" }}>
        <Link
          to="/auth"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "7px 12px",
            background: "var(--ag-surface-2)",
            border: "1px solid var(--ag-rule)",
            borderRadius: 6,
            color: "var(--ag-ink-2)",
            fontSize: 12.5,
            fontWeight: 500,
            textDecoration: "none",
          }}
        >
          {t("auth.signIn")}
        </Link>
      </div>
    );
  }

  const displayName = user.name ?? user.email;
  const avatarText = user.name ? agentInitials(user.name) : "?";
  const roleLine = user.role === "pro" ? "Pro plan" : "Free plan";

  return (
    <div
      ref={menuRef}
      style={{
        position: "relative",
        flex: "none",
        padding: "12px 14px",
        borderTop: "1px solid var(--ag-rule)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {menuOpen && (
        <div
          style={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: "calc(100% + 4px)",
            background: "var(--ag-surface)",
            border: "1px solid var(--ag-rule)",
            borderRadius: 8,
            boxShadow: "0 4px 14px rgba(23,32,29,0.08)",
            zIndex: 50,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--ag-rule)" }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ag-ink)" }}>{displayName}</div>
            <div style={{ fontSize: 10.5, color: "var(--ag-ink-3)" }}>{user.email}</div>
          </div>
          <div style={{ padding: 4 }}>
            <Link
              to="/account"
              onClick={() => setMenuOpen(false)}
              style={{
                display: "block",
                padding: "6px 8px",
                borderRadius: 5,
                fontSize: 12,
                color: "var(--ag-ink-2)",
                textDecoration: "none",
              }}
            >
              {t("auth.account.title")}
            </Link>
            <button
              onClick={() => void handleSignOut()}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "6px 8px",
                borderRadius: 5,
                fontSize: 12,
                color: "var(--ag-ink-2)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t("auth.signOut")}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Account menu"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            background: "var(--ag-accent-soft)",
            color: "var(--ag-accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--ag-font-serif)",
            fontSize: 12,
            fontWeight: 600,
            flex: "none",
          }}
        >
          {avatarText}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "var(--ag-ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--ag-ink-3)" }}>{roleLine}</div>
        </div>
      </button>
      <button
        onClick={() => setMenuOpen((v) => !v)}
        style={{
          background: "transparent",
          border: "1px solid var(--ag-rule)",
          color: "var(--ag-ink-3)",
          padding: "4px 9px",
          borderRadius: 6,
          fontSize: 10.5,
          cursor: "pointer",
          fontFamily: "var(--ag-font-ui)",
          flex: "none",
        }}
      >
        {t("nav.menu", { defaultValue: "Menu" })}
      </button>
    </div>
  );
}

const AGENT_SIDEBAR_COLLAPSED_KEY = STORAGE_UI.agentSidebarCollapsed;

export function AppShellAgent() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AGENT_SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [collapsed]);

  // Lock document scroll for this full-screen shell; restore when navigating away.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = "0";
    document.body.style.left = "0";
    return () => {
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.left = "";
    };
  }, []);

  return (
    <FlowsProvider>
    <div
      // ag-shell scopes the agent-workspace overrides for global
      // utility classes (.input-field / .label-text) so the tool
      // pages (Seller Pitch / Lead CRM) and the listing-detail
      // AI-copy drawer inherit the same outlined form styling as
      // case-detail and /account without each page touching its
      // own DOM.
      className="ag-shell"
      style={{
        ...SHELL_TOKENS,
        display: "flex",
        height: "100dvh",
        width: "100%",
        overflow: "hidden",
        background: "var(--ag-bg)",
        color: "var(--ag-ink)",
      }}
    >
      <div
        className="hidden md:flex"
        style={{
          width: collapsed ? 0 : 264,
          overflow: "hidden",
          transition: "width 200ms ease-out",
          pointerEvents: collapsed ? "none" : undefined,
        }}
        aria-hidden={collapsed}
      >
        <AgentSidebar onCollapse={() => setCollapsed(true)} />
      </div>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="fixed inset-y-0 left-0 z-50 w-[78%] max-w-[320px] md:hidden animate-slide-in-left">
            <AgentSidebar onNavigate={() => setOpen(false)} />
          </div>
        </>
      )}

      <div style={{ position: "relative", display: "flex", flex: 1, minWidth: 0, flexDirection: "column", overflow: "hidden" }}>
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            title="Show sidebar"
            aria-label="Show sidebar"
            className="hidden md:flex animate-fade-in"
            style={{
              position: "absolute",
              left: 12,
              top: 12,
              zIndex: 30,
              width: 32,
              height: 32,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              border: "1px solid var(--ag-rule)",
              background: "var(--ag-surface)",
              color: "var(--ag-ink-3)",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(23,32,29,0.06)",
            }}
          >
            <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
              <rect x="1.5" y="2.5" width="11" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
              <path d="M5.5 2.5v9" stroke="currentColor" strokeWidth="1.3" />
              <path d="M8 5l1.5 2L8 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <header
          className="md:hidden"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
            background: "var(--ag-surface)",
            borderBottom: "1px solid var(--ag-rule)",
            flex: "none",
          }}
        >
          <button
            aria-label={t("nav.menu")}
            onClick={() => setOpen((v) => !v)}
            style={{
              padding: 6,
              borderRadius: 6,
              border: "none",
              background: "transparent",
              color: "var(--ag-ink-2)",
              cursor: "pointer",
            }}
          >
            {open ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
          </button>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <BrandGlyph size={22} />
            <span
              style={{
                fontFamily: "var(--ag-font-serif)",
                fontSize: 15,
                fontWeight: 600,
                color: "var(--ag-ink)",
                letterSpacing: "-0.02em",
              }}
            >
              {t("app.name")}
              <span style={{ color: "var(--ag-accent)" }}>.</span>
            </span>
          </Link>
        </header>

        <EmailVerificationBanner />

        {/* minWidth:0 lets this flex child shrink below its content;
            overflowX:hidden is the backstop so a single over-wide
            descendant can never pan the whole surface horizontally
            (the agent-dashboard "content shifted left" bug). Pages
            own their own vertical scroll via overflowY:auto. */}
        <main style={{ flex: 1, minWidth: 0, minHeight: 0, overflowX: "hidden", overflowY: "auto" }}>
          <Outlet />
        </main>
      </div>
      {/* First-time pro user → welcome modal + create-case CTA. Renders
          inside FlowsProvider so its CTA can call useFlows().openNewCase().
          Self-gates on localStorage + zero-cases check; no-op for
          returning users or anyone with cases. */}
      <ProOnboardingGate />
    </div>
    </FlowsProvider>
  );
}
